import React, { Suspense } from 'react';
import LoadingSpinner from './LoadingSpinner';

// Lazy load heavy components
const Dashboard = React.lazy(() => import('./Dashboard'));
const AdminDashboard = React.lazy(() => import('./AdminDashboard'));
const Payment = React.lazy(() => import('./Payment'));
const RegistrationForm = React.lazy(() => import('./RegistrationForm'));
const EnterpriseLandingPage = React.lazy(() => import('./EnterpriseLandingPage'));
const Levels = React.lazy(() => import('./Levels'));
const Success = React.lazy(() => import('./Success'));
const MonitoringDashboard = React.lazy(() => import('./MonitoringDashboard'));

// Lazy load pages
const TermsAndConditions = React.lazy(() => import('../pages/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('../pages/PrivacyPolicy'));
const RefundsAndCancellations = React.lazy(() => import('../pages/RefundsAndCancellations'));
const ContactUs = React.lazy(() => import('../pages/ContactUs'));
const Services = React.lazy(() => import('../pages/Services'));
const HealthCheck = React.lazy(() => import('../pages/HealthCheck'));

// Higher-order component for lazy loading with error boundary
const withLazyLoading = <P extends object>(
  Component: React.ComponentType<P>,
  fallbackText: string = 'Loading...'
) => {
  return (props: P) => (
    <Suspense fallback={<LoadingSpinner size="lg" text={fallbackText} />}>
      <Component {...props} />
    </Suspense>
  );
};

// Export lazy components with loading states
export const LazyDashboard = withLazyLoading(Dashboard, 'Loading dashboard...');
export const LazyAdminDashboard = withLazyLoading(AdminDashboard, 'Loading admin dashboard...');
export const LazyPayment = withLazyLoading(Payment, 'Loading payment...');
export const LazyRegistrationForm = withLazyLoading(RegistrationForm, 'Loading registration...');
export const LazyEnterpriseLandingPage = withLazyLoading(EnterpriseLandingPage, 'Loading landing page...');
export const LazyLevels = withLazyLoading(Levels, 'Loading levels...');
export const LazySuccess = withLazyLoading(Success, 'Loading success page...');
export const LazyMonitoringDashboard = withLazyLoading(MonitoringDashboard, 'Loading monitoring dashboard...');

// Lazy pages
export const LazyTermsAndConditions = withLazyLoading(TermsAndConditions, 'Loading terms...');
export const LazyPrivacyPolicy = withLazyLoading(PrivacyPolicy, 'Loading privacy policy...');
export const LazyRefundsAndCancellations = withLazyLoading(RefundsAndCancellations, 'Loading refunds...');
export const LazyContactUs = withLazyLoading(ContactUs, 'Loading contact...');
export const LazyServices = withLazyLoading(Services, 'Loading services...');
export const LazyHealthCheck = withLazyLoading(HealthCheck, 'Loading health check...');
